import { Clock, CheckCircle2, AlertTriangle, Users, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { ProgressGridIcon } from './ProgressGridIcon';
import { progressLegendRows } from './DiscoveryGrid';
import type { GridSummary } from '../utils/discoveryGrid';
import { ProgressGridModal } from './ProgressGridModal';
import { useState } from 'react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from './ui/hover-card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

interface ClientEngagementCardProps {
  engagement: {
    companyId: string;
    companyName: string;
    engagementId?: string | null;
    engagementName?: string | null;
    engagementCount?: number;
    companyLogo?: string;
    totalItems: number;
    completedItems: number;
    waitingForConsultant: number;
    waitingForClient: number;
    blockedItems: number;
    progressPercentage: number;
    healthStatus: 'healthy' | 'at-risk' | 'needs-attention';
    spaces: number;
    onboardingComplete: boolean;
    spaceDiscoveryComplete: boolean;
    lastActivity: string | null;
    createdAt: string;
  };
  onViewDetails: (companyId: string) => void;
  accessToken?: string;
  currentUser?: any;
}

export function ClientEngagementCard({ engagement, onViewDetails, accessToken, currentUser }: ClientEngagementCardProps) {
  const [gridModalOpen, setGridModalOpen] = useState(false);
  // undefined = loading, null = failed, object = loaded (reported by ProgressGridIcon)
  const [gridSummary, setGridSummary] = useState<GridSummary | null | undefined>(undefined);

  const getHealthBadge = (status: string) => {
    let config = { color: '', icon: null, text: '' };
    switch (status) {
      case 'healthy':
        config = { color: 'bg-green-50 text-green-700 border-green-200', icon: CheckCircle2, text: 'Healthy' };
        break;
      case 'at-risk':
        config = { color: 'bg-red-50 text-red-700 border-red-200', icon: AlertTriangle, text: 'At Risk' };
        break;
      case 'needs-attention':
        config = { color: 'bg-orange-50 text-orange-700 border-orange-200', icon: Clock, text: 'Needs Attention' };
        break;
      default: return null;
    }
    const Icon = config.icon!;

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="outline" className={`${config.color} truncate max-w-full cursor-help px-2 flex items-center gap-1.5 h-6`}>
              <Icon className="size-3 flex-shrink-0" />
              <span className="truncate">{config.text}</span>
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <p>{config.text}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const formatLastActivity = (lastActivity: string | null) => {
    if (!lastActivity) return 'No recent activity';
    
    const diff = Date.now() - new Date(lastActivity).getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor(diff / (1000 * 60));
    
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking the grid icon
    const target = e.target as HTMLElement;
    if (target.closest('[data-progress-grid-icon]')) {
      return;
    }
    onViewDetails(engagement.companyId);
  };

  const handleGridIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setGridModalOpen(true);
  };


  return (
    <>
      <Card className="hover:shadow-lg transition-shadow cursor-pointer group relative overflow-hidden" onClick={handleCardClick}>
        <CardHeader className="pb-3 px-4 pt-4">
          {/* Deterministic 2-Row Grid Layout */}
          {/* Row 1: Name (Left) | Grid Icon (Right, Fixed 44px) */}
          {/* Row 2: Spaces (Left) | Status Badge (Right) */}
          <div className="grid grid-cols-[minmax(0,1fr)_44px] gap-x-2 gap-y-1.5 w-full">
            
            {/* Row 1 Col 1: Company Name */}
            <div className="flex items-center min-w-0 col-start-1 row-start-1">
              <div className="min-w-0">
                <CardTitle className="text-lg group-hover:text-blue-600 transition-colors truncate leading-tight">
                  {engagement.companyName}
                </CardTitle>
                {(engagement.engagementCount ?? 0) > 1 && engagement.engagementName && (
                  <p className="text-xs text-muted-foreground truncate" title={engagement.engagementName}>
                    {engagement.engagementName}
                  </p>
                )}
              </div>
            </div>

            {/* Row 1 Col 2: Grid Icon (Fixed) */}
            <div className="col-start-2 row-start-1 flex justify-end items-center h-10">
              {accessToken && (
                <HoverCard openDelay={200}>
                  <HoverCardTrigger asChild>
                    <div 
                      data-progress-grid-icon 
                      onClick={handleGridIconClick} 
                      className="size-9 flex items-center justify-center rounded hover:bg-gray-100 hover:ring-1 hover:ring-gray-200 transition-all cursor-pointer"
                    >
                      <ProgressGridIcon
                        companyId={engagement.companyId}
                        accessToken={accessToken}
                        onClick={() => {}}
                        className="opacity-100"
                        onSummary={setGridSummary}
                      />
                    </div>
                  </HoverCardTrigger>
                  <HoverCardContent className="w-80 p-4" align="end" sideOffset={5}>
                    <div className="space-y-4">
                      <div className="space-y-1">
                        <h4 className="text-sm font-semibold leading-none">Discovery Grid Snapshot</h4>
                        <p className="text-xs text-muted-foreground">
                          {gridSummary ? `${gridSummary.total} cells, ${gridSummary.percentLocked}% locked for build` : 'Cells by level'}
                        </p>
                      </div>

                      {gridSummary === undefined ? (
                        <p className="text-xs text-muted-foreground">Loading the grid summary…</p>
                      ) : gridSummary === null ? (
                        <p className="text-xs text-muted-foreground">The grid summary could not be loaded.</p>
                      ) : (
                        <div className="space-y-1.5 text-sm">
                          {progressLegendRows(gridSummary).map((row) => (
                            <div key={row.label} className="flex items-center justify-between gap-3">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="inline-block size-3 shrink-0 rounded-[2px]" style={{ backgroundColor: row.fill, border: row.border ? '1px solid #D1D5DB' : undefined }} />
                                <span className="truncate">{row.label}</span>
                              </div>
                              <span className="font-medium tabular-nums">{row.count}</span>
                            </div>
                          ))}
                          <div className="pt-2 mt-1 border-t text-xs text-muted-foreground space-y-0.5">
                            <div>{gridSummary.court.client} waiting on the client</div>
                            <div>{gridSummary.court.consultant} waiting on the consultant</div>
                          </div>
                        </div>
                      )}

                      <Button 
                        size="sm" 
                        className="w-full" 
                        onClick={(e) => {
                          e.stopPropagation();
                          setGridModalOpen(true);
                        }}
                      >
                        Open Discovery Grid
                      </Button>
                    </div>
                  </HoverCardContent>
                </HoverCard>
              )}
            </div>

            {/* Row 2 Col 1: Spaces Count */}
            <div className="col-start-1 row-start-2 flex items-center min-w-0">
               <p className="text-sm text-muted-foreground truncate leading-tight">
                  {engagement.spaces} {engagement.spaces === 1 ? 'space' : 'spaces'}
               </p>
            </div>

            {/* Row 2 Col 2: Status Badge (Right Aligned, Spanning if needed) */}
            <div className="col-start-1 col-end-3 row-start-2 justify-self-end w-full flex justify-end pointer-events-none">
              <div className="pointer-events-auto max-w-[160px]">
                {getHealthBadge(engagement.healthStatus)}
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Progress */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Progress</span>
              <span className="text-sm font-bold text-blue-600">{engagement.progressPercentage}%</span>
            </div>
            <Progress value={engagement.progressPercentage} className="h-2" />
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-muted-foreground">Completed</span>
                <CheckCircle2 className="size-4 text-green-600" />
              </div>
              <p className="text-xl font-bold text-green-600">
                {engagement.completedItems}/{engagement.totalItems}
              </p>
            </div>

            <div className="bg-orange-50 rounded-lg p-3 border border-orange-100">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-muted-foreground">Your Action</span>
                <Clock className="size-4 text-orange-600" />
              </div>
              <p className="text-xl font-bold text-orange-600">
                {engagement.waitingForConsultant}
              </p>
            </div>

            <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-muted-foreground">Client Action</span>
                <Users className="size-4 text-blue-600" />
              </div>
              <p className="text-xl font-bold text-blue-600">
                {engagement.waitingForClient}
              </p>
            </div>

            {engagement.blockedItems > 0 && (
              <div className="bg-red-50 rounded-lg p-3 border border-red-100">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-muted-foreground">Blocked</span>
                  <AlertTriangle className="size-4 text-red-600" />
                </div>
                <p className="text-xl font-bold text-red-600">
                  {engagement.blockedItems}
                </p>
              </div>
            )}
          </div>

          {/* Last Activity */}
          <div className="flex items-center justify-between text-sm pt-2 border-t">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Clock className="size-4" />
              <span>{formatLastActivity(engagement.lastActivity)}</span>
            </div>
            <Button variant="ghost" size="sm" className="group-hover:bg-blue-50 group-hover:text-blue-600">
              View Details
              <ArrowRight className="size-4 ml-1" />
            </Button>
          </div>

          {/* Onboarding Status Indicators */}
          {(!engagement.onboardingComplete || !engagement.spaceDiscoveryComplete) && (
            <div className="flex gap-2 flex-wrap">
              {!engagement.onboardingComplete && (
                <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200 text-xs">
                  Onboarding Incomplete
                </Badge>
              )}
              {!engagement.spaceDiscoveryComplete && (
                <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 text-xs">
                  Space Discovery Needed
                </Badge>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Discovery grid modal */}
      {gridModalOpen && (
        <ProgressGridModal
          isOpen={gridModalOpen}
          onClose={() => setGridModalOpen(false)}
          companyId={engagement.companyId}
          companyName={engagement.companyName}
          accessToken={accessToken || ''}
          currentUser={currentUser}
        />
      )}
    </>
  );
}